var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Icon, Tooltip, TooltipPortal } from "@animech/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Styled from "./centralized-pump-overlay-content.style";
import { CentralizedPumpComponentInfo } from "./centralized-pump-component-info";
import { EditButtons, } from "ui/components/elements/edit-buttons/edit-buttons";
import { PaddingLessTooltipClassName } from "ui/app/global.style";
import { actions as storeActions, selectors, useAppDispatch, useAppSelector, } from "store";
import { ComponentSection } from "ui/components/panels/canvas/node-components/component-select/component-section";
import { ComponentSelect } from "ui/components/panels/canvas/node-components/component-select/component-select";
import { useNumberConversion } from "hooks/number-conversion";
import { Unit } from "utility/number";
import { systemDataConstants } from "api/system-data";
export var CentralizedPumpOverlayContent = function (_a) {
    var editActions = _a.actions, node = _a.node;
    var _b = __read(useState(), 2), height = _b[0], setHeight = _b[1];
    var _c = __read(useState(false), 2), preRenderCompleted = _c[0], setPreRenderCompleted = _c[1];
    var _d = __read(useState(false), 2), sectionOpen = _d[0], setSectionOpen = _d[1];
    var ref = useRef(null);
    var tooltipResizeObserverRef = useRef();
    var updateRef = useRef();
    var tooltipContentRef = useCallback(function (node) {
        var _a;
        (_a = tooltipResizeObserverRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        if (node) {
            tooltipResizeObserverRef.current = new ResizeObserver(function () {
                var _a;
                (_a = updateRef.current) === null || _a === void 0 ? void 0 : _a.call(updateRef);
            });
            tooltipResizeObserverRef.current.observe(node);
        }
    }, []);
    var shouldBeOpenInitially = !sectionOpen;
    var _e = __read(useState(shouldBeOpenInitially), 2), open = _e[0], setOpen = _e[1];
    var t = useTranslation().t;
    var dispatch = useAppDispatch();
    var convertNumber = useNumberConversion();
    var nodeIds = useMemo(function () { return [node.id]; }, [node.id]);
    var systemDataDefineParameters = useAppSelector(selectors.selectSystemDataDefineParameters);
    var currentlySelectedPumpValues = useAppSelector(selectors.selectSelectedCentralPumps(nodeIds)).map(function (pump) { return pump === null || pump === void 0 ? void 0 : pump.value; });
    var pumpOptions = useMemo(function () {
        if (!(systemDataDefineParameters === null || systemDataDefineParameters === void 0 ? void 0 : systemDataDefineParameters.vacuumPumpComponents))
            return [];
        return Object.values(systemDataDefineParameters.vacuumPumpComponents).filter(function (component) {
            return (component.valid ||
                currentlySelectedPumpValues.includes(component.value)) &&
                (!currentlySelectedPumpValues.length ||
                    currentlySelectedPumpValues.every(function (pump) { return pump === undefined; }) ||
                    component.value !==
                        systemDataConstants.parameterValues.noPumpSelected);
        });
    }, [
        currentlySelectedPumpValues,
        systemDataDefineParameters === null || systemDataDefineParameters === void 0 ? void 0 : systemDataDefineParameters.vacuumPumpComponents,
    ]);
    var createLabel = useCallback(function (component) {
        return component.value === systemDataConstants.parameterValues.noPumpSelected
            ? component.label
            : component.label +
                " (rec ".concat(convertNumber(Number(component.recFeedPressureMpa), Unit.MPA)
                    .formatted, ")");
    }, [convertNumber]);
    var formattedItemLabels = useMemo(function () { return pumpOptions.map(createLabel); }, [createLabel, pumpOptions]);
    var selectedLabels = useMemo(function () {
        return currentlySelectedPumpValues
            .map(function (value) { return pumpOptions.find(function (option) { return option.value === value; }); })
            .filter(function (option) { return option !== undefined; }).map(createLabel);
    }, [createLabel, currentlySelectedPumpValues, pumpOptions]);
    var values = useMemo(function () { return pumpOptions.map(function (option) { return option.value; }); }, [pumpOptions]);
    var selectedValues = useMemo(function () {
        return currentlySelectedPumpValues.filter(function (v) { return v !== undefined; });
    }, [currentlySelectedPumpValues]);
    var onSelectPump = useCallback(function (pump) {
        dispatch(storeActions.selectCentralPump({ nodeIds: nodeIds, value: pump }));
    }, [dispatch, nodeIds]);
    useEffect(function () {
        setTimeout(function () {
            setPreRenderCompleted(true);
        });
    }, []);
    useEffect(function () {
        var observer = new ResizeObserver(function (_a) {
            var _b = __read(_a, 1), entry = _b[0];
            setHeight(entry.contentRect.height);
        });
        if (ref.current)
            observer.observe(ref.current);
        return function () { return observer.disconnect(); };
    }, []);
    useEffect(function () {
        return function () { var _a; return (_a = tooltipResizeObserverRef.current) === null || _a === void 0 ? void 0 : _a.disconnect(); };
    }, []);
    return (_jsx(TooltipPortal, { placement: "right", open: sectionOpen, updateRef: updateRef, anchorElement: _jsx("div", { style: {
                pointerEvents: "auto",
                visibility: preRenderCompleted ? "visible" : "hidden",
            }, onWheel: function (e) { return e.stopPropagation(); }, onMouseDown: function (e) { return e.stopPropagation(); }, children: _jsxs(Styled.Root, { ref: ref, children: [_jsx(EditButtons, { actions: editActions }), _jsxs(Styled.ComponentSelectionRoot, { children: [_jsx(Styled.ChevronContainer, { children: _jsx(Styled.Chevron, { onClick: function () { return setOpen(!open); }, children: _jsx(Icon, { name: open ? "chevron-up" : "chevron-down", color: "primary" }) }) }), _jsx(Styled.SectionList, { state: open || !preRenderCompleted ? "open" : "closed", children: _jsx(ComponentSection, { header: t("sidebar.edit_pump.pump_dropdown_label"), selected: sectionOpen, onClick: function () { return setSectionOpen(!sectionOpen); }, value: selectedLabels.length > 0 ? selectedLabels[0] : undefined }) })] }), _jsx(CentralizedPumpComponentInfo, { nodeId: node.id, selectedPumpLabel: selectedLabels.length > 0 ? selectedLabels[0] : undefined })] }) }), children: _jsx(Tooltip, { className: PaddingLessTooltipClassName, color: "neutral", children: _jsx(Styled.TooltipContent, { ref: tooltipContentRef, style: { minHeight: height }, children: _jsx(ComponentSelect, { labels: formattedItemLabels, values: values, selectedValues: selectedValues, onValueChange: onSelectPump, scrollIntoPosition: "nearest" }) }) }) }));
};
